import React, { useEffect, useState, useMemo, useCallback } from 'react';
import UserMetricsPanel from '../common/UserMetricsPanel';
import { SCORING_API_BASE_URL, authHeaders } from '../../services/api';
import { loadMakeupSessionsForExamSession, loadFlashcardDashboardVignettes } from '../../utils/makeupSessionStorage';
import {
    applyDashboardRevisionStrategy,
    granularKeysForKeywordRow,
} from '../../utils/dashboardRevisionBridge';
import { getValidationThresholdForExam } from '../../config/scoringConfig';

function DashRowActions({ onQcm, onFlash, disabled }) {
    return (
        <div className="flex shrink-0 items-center gap-0.5">
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onQcm();
                }}
                disabled={disabled}
                title="Open QCM practice with this filter"
                className="rounded-md border border-teal-700/60 px-1.5 py-0.5 text-[10px] font-semibold text-teal-200/95 hover:bg-teal-950/60 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="QCM practice"
            >
                QCM
            </button>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onFlash();
                }}
                disabled={disabled}
                title="Flash card review: same filter, study mode"
                className="rounded-md border border-amber-700/60 px-1 py-0.5 text-[9px] font-semibold leading-[10px] text-amber-200/95 hover:bg-amber-950/50 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Flash card review"
            >
                <span className="inline-block text-center">
                    Flash
                    <br />
                    card
                </span>
            </button>
        </div>
    );
}

function Sparkline({ points, width = 280, height = 80 }) {
    const path = useMemo(() => {
        if (!points?.length) return '';
        const xs = points.map((p) => p.day);
        const ys = points.map((p) => p.cumulative_sessions);
        const maxX = Math.max(...xs, 1);
        const maxY = Math.max(...ys, 1);
        const pad = 4;
        const w = width - pad * 2;
        const h = height - pad * 2;
        return points
            .map((p, i) => {
                const x = pad + (p.day / maxX) * w;
                const y = pad + h - (p.cumulative_sessions / maxY) * h;
                return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
            })
            .join(' ');
    }, [points, width, height]);

    if (!points?.length) {
        return <div className="text-xs text-gray-500">No data</div>;
    }

    return (
        <svg width={width} height={height} className="text-emerald-400/90">
            <path d={path} fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
    );
}

/**
 * Dashboard mobile : mêmes métriques globales que l’écran résultat (UserMetricsPanel) + vignettes domain/task / make-up.
 */
const MobileDashboard = ({ sessionContext, onClose }) => {
    const [dashboard, setDashboard] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [flashVignetteTick, setFlashVignetteTick] = useState(0);

    useEffect(() => {
        const bump = () => setFlashVignetteTick((t) => t + 1);
        window.addEventListener('focus', bump);
        document.addEventListener('visibilitychange', bump);
        return () => {
            window.removeEventListener('focus', bump);
            document.removeEventListener('visibilitychange', bump);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            if (!sessionContext?.user || !sessionContext?.exam) {
                setLoading(false);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const makeup = loadMakeupSessionsForExamSession(
                    sessionContext.user,
                    sessionContext.exam,
                    sessionContext.sessionId
                );
                const res = await fetch(`${SCORING_API_BASE_URL}/api/dashboard/compute`, {
                    method: 'POST',
                    headers: authHeaders(),
                    body: JSON.stringify({
                        user_id: sessionContext.user,
                        exam_id: sessionContext.exam,
                        session_id: sessionContext.sessionId,
                        makeup_sessions: makeup,
                    }),
                });
                const data = await res.json();
                if (cancelled) return;
                if (data.success && data.dashboard) {
                    setDashboard(data.dashboard);
                } else {
                    setError(data.error || 'Dashboard unavailable');
                }
            } catch (e) {
                if (!cancelled) setError(e.message || 'Network error');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [sessionContext?.user, sessionContext?.exam, sessionContext?.sessionId]);

    const scores = dashboard?.scores;
    const timeline = dashboard?.makeup_timeline;
    const ongoing = dashboard?.makeup_ongoing;
    const asyncEvolution = dashboard?.async_evolution;
    const makeupEvolution = dashboard?.makeup_evolution;
    const catalogProgress = dashboard?.subdomain_catalog_progress || [];
    const consolidationEstimate = dashboard?.consolidation_time_estimate;
    const validationThreshold = getValidationThresholdForExam(sessionContext?.exam);

    const formatHoursEn = (h) => {
        if (h == null || Number.isNaN(Number(h))) return '—';
        const v = Number(h);
        if (v <= 0) return '0 h';
        if (v < 1) return `${Math.max(1, Math.round(v * 60))} min`;
        return `${v.toFixed(1)} h`;
    };

    const formatAvgMinutesFromSeconds = (sec) => {
        if (sec == null || sec === '') return '—';
        const s = Number(sec);
        if (!Number.isFinite(s) || s <= 0) return '—';
        const min = s / 60;
        if (min < 10) return `${min.toFixed(1)} min`;
        return `${Math.round(min)} min`;
    };

    const numOrNull = (v) => {
        if (v == null || v === '') return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    };

    const normalizeDomainLabel = (raw) => {
        const s = String(raw || '').trim();
        if (!s) return '(unknown)';
        if (s.includes(' - ')) return s.split(' - ', 1)[0].trim() || s;
        return s;
    };

    /** Tri DESC numérique + regroupement strict sur domaine (sans suffixes sous-domaines) */
    const scoresByDomainSorted = useMemo(() => {
        const rows = scores?.scores_by_domain;
        if (!rows?.length) return [];
        const grouped = new Map();
        rows.forEach((r) => {
            const key = normalizeDomainLabel(r.domain);
            const avg = numOrNull(r.avg_score);
            const count = Math.max(0, Number(r.count || 0));
            if (!grouped.has(key)) {
                grouped.set(key, { domain: key, sum: 0, count: 0 });
            }
            const g = grouped.get(key);
            if (avg != null && count > 0) {
                g.sum += avg * count;
                g.count += count;
            }
        });
        const merged = Array.from(grouped.values()).map((g) => ({
            domain: g.domain,
            count: g.count,
            avg_score: g.count > 0 ? Number((g.sum / g.count).toFixed(2)) : null,
        }));
        return merged.sort((a, b) => {
            const na = numOrNull(a.avg_score);
            const nb = numOrNull(b.avg_score);
            if (na == null && nb == null) return 0;
            if (na == null) return 1;
            if (nb == null) return -1;
            return nb - na;
        });
    }, [scores]);

    const scoresByDomainTaskSorted = useMemo(() => {
        const rows = scores?.scores_by_domain_task;
        if (!rows?.length) return [];
        return [...rows].sort((a, b) => {
            const na = numOrNull(a.avg_score);
            const nb = numOrNull(b.avg_score);
            if (na == null && nb == null) return 0;
            if (na == null) return 1;
            if (nb == null) return -1;
            return nb - na;
        });
    }, [scores]);

    /** ASC sur moyenne — sous-domaines à renforcer (API: weakest_subdomains) */
    const weakestSubdomains = useMemo(() => {
        const fromApi = scores?.weakest_subdomains ?? scores?.weakest_domains;
        if (!fromApi?.length) return [];
        return [...fromApi]
            .filter((r) => {
                const v = numOrNull(r.avg_score);
                return v != null && v < validationThreshold;
            })
            .sort((a, b) => {
            const na = numOrNull(a.avg_score);
            const nb = numOrNull(b.avg_score);
            if (na == null && nb == null) return 0;
            if (na == null) return 1;
            if (nb == null) return -1;
            return na - nb;
        });
    }, [scores, validationThreshold]);

    const evolutionPoints = asyncEvolution?.chunks || [];
    const expectedChunks = Number(asyncEvolution?.expected_chunks || 0);
    const qcmThresholdPct = Number(asyncEvolution?.qcm_validation_threshold_10 || validationThreshold) * 10;
    const evolutionAvgPath = useMemo(() => {
        if (!evolutionPoints.length) return '';
        const pad = 6;
        const w = 280;
        const h = 80;
        const maxX = Math.max(...evolutionPoints.map((p) => p.chunk_index), 1);
        const maxY = 100;
        return evolutionPoints
            .map((p, i) => {
                const x = pad + ((p.chunk_index || i + 1) / maxX) * (w - pad * 2);
                const y = pad + (1 - Number(p.avg_percent_100 || 0) / maxY) * (h - pad * 2);
                return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
            })
            .join(' ');
    }, [evolutionPoints]);

    const evolutionValidatedPath = useMemo(() => {
        if (!evolutionPoints.length) return '';
        const pad = 6;
        const w = 280;
        const h = 80;
        const maxX = Math.max(...evolutionPoints.map((p) => p.chunk_index), 1);
        const maxY = 100;
        return evolutionPoints
            .map((p, i) => {
                const x = pad + ((p.chunk_index || i + 1) / maxX) * (w - pad * 2);
                const weak = Number(p.weak_answers_count || 0);
                const count = Math.max(1, Number(p.questions_count || 0));
                const validatedPct = Math.max(0, Math.min(100, ((count - weak) / count) * 100));
                const y = pad + (1 - validatedPct / maxY) * (h - pad * 2);
                return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
            })
            .join(' ');
    }, [evolutionPoints]);

    const chunkTickMarks = useMemo(() => {
        const maxChunks = Math.max(expectedChunks || evolutionPoints.length || 1, 1);
        const step = maxChunks <= 15 ? 3 : 5;
        const ticks = [];
        for (let c = 1; c <= maxChunks; c += step) {
            ticks.push(c);
        }
        if (ticks[ticks.length - 1] !== maxChunks) ticks.push(maxChunks);
        return { ticks, maxChunks, step };
    }, [expectedChunks, evolutionPoints.length]);
    const totalQuestionsNumber = useMemo(() => {
        if (evolutionPoints.length > 0) {
            return evolutionPoints.reduce((sum, p) => sum + Math.max(0, Number(p.questions_count || 0)), 0);
        }
        return Math.max(0, Number(scores?.total_qcm_answered || 0));
    }, [evolutionPoints, scores?.total_qcm_answered]);

    const sessionForecastChart = useMemo(() => {
        const w = 280;
        const h = 180;
        const padL = 16;
        const padR = 8;
        const topY = 8;
        const topH = 94;
        const botY = 112;
        const botH = 56;
        const totalVals = evolutionPoints.map((p) => Number(p.chunk_total_estimated_seconds || p.avg_estimated_total_seconds || 0));
        const errVals = evolutionPoints.map((p) => Number(p.weak_answers_count || 0));
        const avgQVals = evolutionPoints.map((p) => Number(p.avg_elapsed_per_question_seconds || 0));
        const validVals = evolutionPoints.map((p) => {
            const weak = Number(p.weak_answers_count || 0);
            const count = Math.max(1, Number(p.questions_count || 0));
            return Math.max(0, Math.min(100, ((count - weak) / count) * 100));
        });

        const maxStack = Math.max(...totalVals, 1);
        const innerW = w - padL - padR;
        const slot = innerW / Math.max(1, evolutionPoints.length);
        const barW = Math.max(5, slot * 0.46);
        const topBase = topY + topH;
        const botBase = botY + botH;
        const clampY = (y, minY, maxY) => Math.max(minY + 1, Math.min(maxY - 1, y));

        const makeScale = (vals, minY, maxY) => {
            const vmin = Math.min(...vals, 0);
            const vmax = Math.max(...vals, 1);
            if (vmax - vmin < 1e-6) {
                return () => (minY + maxY) / 2;
            }
            const pad = (vmax - vmin) * 0.1;
            const lo = Math.max(0, vmin - pad);
            const hi = vmax + pad;
            return (v) => {
                const t = (v - lo) / Math.max(1e-6, hi - lo);
                return clampY(minY + (1 - t) * (maxY - minY), minY, maxY);
            };
        };

        const toTopYValid = makeScale(validVals, topY, topBase);
        const toBotYErr = makeScale(errVals, botY, botBase);
        const toBotYAvgQ = makeScale(avgQVals, botY, botBase);
        const thresholdY = clampY(topY + (1 - qcmThresholdPct / 100) * topH, topY, topBase);

        const timeBars = evolutionPoints.map((p, i) => {
            const total = Math.max(1, Number(p.chunk_total_estimated_seconds || p.avg_estimated_total_seconds || 0));
            const invested = Math.max(0, Math.min(total, Number(p.avg_time_invested_seconds || 0)));
            const toReady = Math.max(0, total - invested);
            const x = padL + i * slot + (slot - barW) / 2;
            const totalH = (total / maxStack) * topH;
            const investedH = (invested / maxStack) * topH;
            const toReadyH = Math.max(1, totalH - investedH);
            return {
                x,
                yInvested: topBase - investedH,
                hInvested: Math.max(1, investedH),
                yToReady: topBase - totalH,
                hToReady: Math.max(1, toReadyH),
                label: i + 1,
            };
        });

        const validPts = evolutionPoints.map((p, i) => {
            const weak = Number(p.weak_answers_count || 0);
            const count = Math.max(1, Number(p.questions_count || 0));
                const vPct = Math.max(0, Math.min(100, ((count - weak) / count) * 100));
            const x = padL + i * slot + slot / 2;
                return { x, y: toTopYValid(vPct) };
        });
        const validCurve = [
            `M ${(padL).toFixed(1)} ${(topBase).toFixed(1)}`,
            ...validPts.map((p) => `L ${p.x.toFixed(1)} ${p.y.toFixed(1)}`),
        ].join(' ');

        const errCurve = evolutionPoints
            .map((p, i) => {
            const err = Math.max(0, Number(p.weak_answers_count || 0));
                const x = padL + i * slot + slot / 2;
                const y = toBotYErr(err);
                return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
            })
            .join(' ');
        const avgQTimeCurve = evolutionPoints
            .map((p, i) => {
                const q = Math.max(0, Number(p.avg_elapsed_per_question_seconds || 0));
                const x = padL + i * slot + slot / 2;
                const y = toBotYAvgQ(q);
                return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
            })
            .join(' ');

        return {
            w, h, topBase, botBase, thresholdY, timeBars, validCurve, errCurve, avgQTimeCurve,
        };
    }, [evolutionPoints, qcmThresholdPct, asyncEvolution?.latest]);

    const weakLoadBars = useMemo(() => {
        const w = 280;
        const h = 110;
        const padL = 14;
        const padR = 10;
        const padT = 8;
        const padB = 20;
        const values = evolutionPoints.map((p) => Math.max(0, Number(p.weak_answers_count || 0)));
        const maxY = Math.max(...values, 1);
        const innerW = w - padL - padR;
        const innerH = h - padT - padB;
        const slot = innerW / Math.max(1, evolutionPoints.length);
        const barW = Math.max(6, slot * 0.65);
        return evolutionPoints.map((p, i) => {
            const v = Math.max(0, Number(p.weak_answers_count || 0));
            const bh = (v / maxY) * innerH;
            const x = padL + i * slot + (slot - barW) / 2;
            const y = padT + (innerH - bh);
            return {
                x: Number(x.toFixed(1)),
                y: Number(y.toFixed(1)),
                w: Number(barW.toFixed(1)),
                h: Number(Math.max(1, bh).toFixed(1)),
                label: `Q${p.question_start}-Q${p.question_end}`,
                value: v,
            };
        });
    }, [evolutionPoints]);
    const weakLoadMax = useMemo(
        () => Math.max(0, ...evolutionPoints.map((p) => Number(p.weak_answers_count || 0))),
        [evolutionPoints]
    );

    const latestChunk = asyncEvolution?.latest || null;
    const weakRatePct = useMemo(() => {
        if (!latestChunk?.questions_count) return 0;
        return Math.round((Number(latestChunk.weak_answers_count || 0) / Number(latestChunk.questions_count || 1)) * 100);
    }, [latestChunk]);

    const scoreBands = useMemo(() => {
        const rows = scoresByDomainTaskSorted || [];
        let high = 0;
        let mid = 0;
        let low = 0;
        rows.forEach((r) => {
            const v = numOrNull(r.avg_score);
            if (v == null) return;
            if (v >= validationThreshold) high += 1;
            else if (v >= 0) mid += 1;
            else low += 1;
        });
        const total = Math.max(1, high + mid + low);
        return {
            high,
            mid,
            low,
            highPct: Math.round((high / total) * 100),
            midPct: Math.round((mid / total) * 100),
            lowPct: Math.round((low / total) * 100),
        };
    }, [scoresByDomainTaskSorted, validationThreshold]);

    const domainHeatRows = useMemo(() => {
        return (scoresByDomainSorted || []).slice(0, 8).map((r) => {
            const v = numOrNull(r.avg_score) ?? 0;
            const pct = Math.max(0, Math.min(100, Math.round(((v + 2) / 12) * 100)));
            return { ...r, heatPct: pct };
        });
    }, [scoresByDomainSorted]);

    const makeupPoints = makeupEvolution?.points || [];
    const makeupBuckets = makeupEvolution?.buckets || [];
    const makeupCurvePaths = useMemo(() => {
        if (!makeupPoints.length) return { initial: '', remaining: '' };
        const pad = 6;
        const w = 280;
        const h = 80;
        const maxX = Math.max(...makeupPoints.map((p) => p.question_count || 0), 1);
        const maxY = Math.max(
            ...makeupPoints.map((p) =>
                Math.max(Number(p.initial_total || 0), Number(p.remaining_total || 0))
            ),
            1
        );
        const mkPath = (key) =>
            makeupPoints
                .map((p, i) => {
                    const x = pad + ((Number(p.question_count || i + 1) || i + 1) / maxX) * (w - pad * 2);
                    const y = pad + (1 - Number(p[key] || 0) / maxY) * (h - pad * 2);
                    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
                })
                .join(' ');
        return { initial: mkPath('initial_total'), remaining: mkPath('remaining_total') };
    }, [makeupPoints]);

    const uid = sessionContext?.user || 'anonymous_user';

    const flashDashboardVignettes = useMemo(
        () => loadFlashcardDashboardVignettes(uid),
        [uid, flashVignetteTick]
    );

    const applyDomainRow = useCallback(
        (domain, studyMode) => {
            if (!domain) return;
            applyDashboardRevisionStrategy(uid, sessionContext?.exam, sessionContext?.sessionId, {
                filterType: 'manual_domain',
                domainSelection: [domain],
                studyMode,
            });
            onClose();
        },
        [uid, sessionContext?.exam, sessionContext?.sessionId, onClose]
    );

    const applySubdomainRow = useCallback(
        (task, studyMode) => {
            if (!task) return;
            applyDashboardRevisionStrategy(uid, sessionContext?.exam, sessionContext?.sessionId, {
                filterType: 'manual_task',
                taskSelection: [task],
                studyMode,
            });
            onClose();
        },
        [uid, sessionContext?.exam, sessionContext?.sessionId, onClose]
    );

    const applyKeywordRow = useCallback(
        (keywordsKey, studyMode) => {
            const gk = granularKeysForKeywordRow(uid, keywordsKey);
            if (!gk.length) return;
            applyDashboardRevisionStrategy(uid, sessionContext?.exam, sessionContext?.sessionId, {
                filterType: 'weakness_granular',
                granularKeys: gk,
                studyMode,
            });
            onClose();
        },
        [uid, sessionContext?.exam, sessionContext?.sessionId, onClose]
    );

    return (
        <div className="min-h-full bg-[#1a1a1a] text-gray-200 pb-24">
            <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-gray-800 bg-[#1a1a1a]/95 px-4 py-3 backdrop-blur">
                <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg border border-gray-600 px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800"
                >
                    ← Back
                </button>
                <h1 className="text-lg font-semibold text-gray-100">Dashboard</h1>
            </div>

            <div className="px-4 pt-4 space-y-4">
                {/* Même bloc métriques que l’écran résultat (taux, fenêtres 10/30/60…) */}
                <UserMetricsPanel sessionContext={sessionContext} className="mb-0" />

                {flashDashboardVignettes.length > 0 && (
                    <div className="rounded-lg border border-amber-700/35 bg-amber-950/25 px-4 py-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-amber-200/90 mb-2">
                            Recent flash card ratings
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {flashDashboardVignettes.slice(0, 4).map((v, i) => (
                                <div
                                    key={`${v.id}-${v.at}-${i}`}
                                    className="min-w-0 max-w-[48%] flex-1 rounded-md border border-amber-600/30 bg-black/25 px-2 py-1.5 text-[11px] text-amber-50/95"
                                    title={v.label}
                                >
                                    <span className="block truncate text-amber-100/80">{v.label}</span>
                                    <span className="block truncate font-mono text-[10px] text-gray-500">{v.id}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {loading && (
                    <div className="rounded-lg border border-gray-700 bg-gray-900/50 px-4 py-6 text-center text-sm text-gray-400">
                        Loading analytics…
                    </div>
                )}
                {error && (
                    <div className="rounded-lg border border-red-900/50 bg-red-950/30 px-4 py-3 text-sm text-red-300">
                        {error}
                    </div>
                )}

                {!loading && scores && (
                    <>
                        {/* Même gabarit que UserMetricsPanel « Stats » (teal, pas purple) */}
                        <div className="rounded-lg border border-teal-600/30 bg-teal-900/20 p-4">
                            <div className="flex items-center mb-3">
                                <svg
                                    className="w-5 h-5 text-teal-400 mr-2 shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                                    />
                                </svg>
                                <span className="text-lg font-semibold text-teal-300">Scores by domain</span>
                            </div>
                            <div className="border-t border-teal-600/30 mb-3" />
                            <div className="text-xs text-gray-400 mb-2">
                                Avg score /10 · {scores.total_qcm_answered} QCM recorded for this exam
                            </div>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {scoresByDomainSorted?.length ? (
                                    scoresByDomainSorted.map((row) => (
                                        <div
                                            key={row.domain}
                                            className="flex gap-2 items-start justify-between border-b border-teal-600/20 pb-2 last:border-0"
                                        >
                                            <div className="flex min-w-0 flex-1 items-start gap-1">
                                                <DashRowActions
                                                    onQcm={() => applyDomainRow(row.domain, 'qcm')}
                                                    onFlash={() => applyDomainRow(row.domain, 'flashcard')}
                                                    disabled={!row.domain}
                                                />
                                                <span
                                                    className="min-w-0 flex-1 text-xs text-teal-100/90 leading-snug [text-wrap:pretty] line-clamp-3 break-words pl-1 border-l-2 border-teal-500/50"
                                                    title={row.domain}
                                                >
                                                    {row.domain}
                                                </span>
                                            </div>
                                            <div className="flex shrink-0 items-baseline gap-2 text-xs tabular-nums">
                                                <span className="text-gray-500">{row.count}×</span>
                                                <span className="font-mono text-teal-200 font-semibold w-10 text-right">
                                                    {row.avg_score != null ? row.avg_score : '—'}
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-xs text-gray-500">No domain-tagged scores yet.</div>
                                )}
                            </div>
                        </div>

                        <div className="rounded-lg border border-cyan-600/30 bg-cyan-900/20 p-4">
                            <div className="flex items-center mb-3">
                                <svg
                                    className="w-5 h-5 text-cyan-400 mr-2 shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 6h16M4 10h16M4 14h10M4 18h10"
                                    />
                                </svg>
                                <span className="text-lg font-semibold text-cyan-300">Scores by sub-domain</span>
                            </div>
                            <div className="border-t border-cyan-600/30 mb-3" />
                            <div className="space-y-2 max-h-56 overflow-y-auto">
                                {scoresByDomainTaskSorted?.length ? (
                                    scoresByDomainTaskSorted.map((row, idx) => (
                                        <div
                                            key={`${row.domain}|${row.task}|${idx}`}
                                            className="border-b border-cyan-600/20 pb-2 last:border-0"
                                        >
                                            <div className="text-[11px] text-cyan-200/70 leading-snug line-clamp-2 break-words pl-1 border-l-2 border-cyan-500/40 mb-1">
                                                {row.domain}
                                            </div>
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex min-w-0 flex-1 items-start gap-1">
                                                    <DashRowActions
                                                        onQcm={() => applySubdomainRow(row.task, 'qcm')}
                                                        onFlash={() => applySubdomainRow(row.task, 'flashcard')}
                                                        disabled={!row.task}
                                                    />
                                                    <span className="min-w-0 flex-1 text-xs text-cyan-100/90 leading-snug line-clamp-3 break-words pl-1 border-l-2 border-cyan-500/30">
                                                        {row.task}
                                                    </span>
                                                </div>
                                                <div className="flex shrink-0 items-baseline gap-2 text-xs tabular-nums">
                                                    <span className="text-gray-500">{row.count}×</span>
                                                    <span className="font-mono text-cyan-200 font-semibold w-10 text-right">
                                                        {row.avg_score != null ? row.avg_score : '—'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-xs text-gray-500">No sub-domain data yet.</div>
                                )}
                            </div>
                        </div>

                        <div className="rounded-lg border border-slate-600/35 bg-slate-900/25 p-4">
                            <div className="flex items-center mb-3">
                                <svg
                                    className="w-5 h-5 text-slate-400 mr-2 shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 6h16M4 10h16M4 14h10M4 18h10"
                                    />
                                </svg>
                                <span className="text-lg font-semibold text-slate-200">
                                    Questions asked by sub-domain (catalog)
                                </span>
                            </div>
                            <div className="border-t border-slate-600/30 mb-3" />
                            <div className="text-xs text-gray-500 mb-2">
                                Sub-domains from exam metadata catalog (not inferred from your answers only).
                            </div>
                            <div className="space-y-2 max-h-72 overflow-y-auto">
                                {catalogProgress.length ? (
                                    catalogProgress.map((row) => (
                                        <div
                                            key={`cat-${row.domain}|${row.task}`}
                                            className="flex items-start justify-between gap-2 border-b border-slate-700/40 pb-2 last:border-0"
                                        >
                                            <div className="flex min-w-0 flex-1 items-start gap-1">
                                                <DashRowActions
                                                    onQcm={() => applySubdomainRow(row.task, 'qcm')}
                                                    onFlash={() => applySubdomainRow(row.task, 'flashcard')}
                                                    disabled={!row.task}
                                                />
                                                <div className="min-w-0 flex-1">
                                                    {row.domain ? (
                                                        <div className="text-[11px] text-slate-400 leading-snug line-clamp-2 break-words pl-1 border-l-2 border-slate-500/50 mb-0.5">
                                                            {row.domain}
                                                        </div>
                                                    ) : null}
                                                    <div className="text-xs text-slate-100/90 leading-snug line-clamp-2 break-words pl-1 border-l-2 border-slate-500/35">
                                                        {row.task}
                                                    </div>
                                                </div>
                                            </div>
                                            <span className="shrink-0 font-mono text-sm text-slate-200 tabular-nums">
                                                {row.asked_count}×
                                            </span>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-xs text-gray-500">No catalog sub-domains for this exam.</div>
                                )}
                            </div>
                        </div>

                        {consolidationEstimate ? (
                            <div className="rounded-lg border border-indigo-600/35 bg-indigo-950/25 p-4">
                                <div className="flex items-center mb-3">
                                    <svg
                                        className="w-5 h-5 text-indigo-400 mr-2 shrink-0"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                    <span className="text-lg font-semibold text-indigo-200 leading-snug">
                                        Estimated time to consolidate your knowledge
                                    </span>
                                </div>
                                <div className="border-t border-indigo-600/30 mb-3" />
                                <div className="text-xs text-gray-400 mb-3 space-y-2">
                                    <p>
                                        Average time per question (up to 10 recent answers, from time actually
                                        spent):{' '}
                                        <span className="font-mono text-indigo-200">
                                            {formatAvgMinutesFromSeconds(
                                                consolidationEstimate.avg_expected_seconds_per_question
                                            )}
                                        </span>
                                    </p>
                                    <p>Your estimated completion time is the sum of three factors:</p>
                                    <ul className="list-disc pl-4 space-y-1">
                                        <li>addressing under-represented topics in your history,</li>
                                        <li>remediating weaknesses,</li>
                                        <li>and closing the gap to reach the required passing score.</li>
                                    </ul>
                                    <p>
                                        Please note: These timeframes may overlap; however, this figure provides
                                        a maximum estimate for solidifying your expertise.
                                    </p>
                                </div>
                                <ul className="space-y-2 text-sm text-gray-200">
                                    <li className="flex justify-between gap-2">
                                        <span className="text-gray-400">Remaining exam scope ({consolidationEstimate.subdomain_questions_remaining} Q)</span>
                                        <span className="font-mono text-indigo-200 shrink-0">
                                            {formatHoursEn(consolidationEstimate.estimated_hours_subdomain_practice)}
                                        </span>
                                    </li>
                                    <li className="flex justify-between gap-2">
                                        <span className="text-gray-400">
                                            Make-up queue ({consolidationEstimate.makeup_questions_remaining} Q)
                                        </span>
                                        <span className="font-mono text-indigo-200 shrink-0">
                                            {formatHoursEn(consolidationEstimate.estimated_hours_makeup)}
                                        </span>
                                    </li>
                                    <li className="flex justify-between gap-2">
                                        <span className="text-gray-400">Stats — time to ready</span>
                                        <span className="font-mono text-indigo-200 shrink-0">
                                            {consolidationEstimate.stats_time_to_ready_hours != null
                                                ? formatHoursEn(consolidationEstimate.stats_time_to_ready_hours)
                                                : 'n/a'}
                                        </span>
                                    </li>
                                </ul>
                                <div className="mt-3 pt-3 border-t border-indigo-600/30 flex justify-between items-baseline gap-2">
                                    <span className="text-sm font-medium text-gray-300">Total (sum)</span>
                                    <span className="text-lg font-semibold font-mono text-indigo-100">
                                        {formatHoursEn(consolidationEstimate.estimated_total_hours)}
                                    </span>
                                </div>
                            </div>
                        ) : null}

                        {weakestSubdomains.length > 0 && (
                            <div className="rounded-lg border border-amber-600/30 bg-amber-950/25 p-4">
                                <div className="flex items-center mb-3">
                                    <svg
                                        className="w-5 h-5 text-amber-400 mr-2 shrink-0"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                        />
                                    </svg>
                                    <span className="text-lg font-semibold text-amber-200/90">
                                        Sub-domain to strengthen
                                    </span>
                                </div>
                                <div className="border-t border-amber-600/30 mb-3" />
                                <div className="text-xs text-gray-500 mb-2">
                                    Sorted by average score (asc) · only avg below threshold ({validationThreshold}/10)
                                </div>
                                <ul className="space-y-2 text-xs text-amber-100/90 max-h-96 overflow-y-auto">
                                    {weakestSubdomains.map((w) => (
                                        <li
                                            key={
                                                w.task != null && w.task !== ''
                                                    ? `${w.domain}|${w.task}`
                                                    : w.domain
                                            }
                                            className="border-b border-amber-600/20 pb-2 last:border-0"
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex min-w-0 flex-1 items-start gap-1">
                                                    <DashRowActions
                                                        onQcm={() =>
                                                            w.task
                                                                ? applySubdomainRow(w.task, 'qcm')
                                                                : applyDomainRow(w.domain, 'qcm')
                                                        }
                                                        onFlash={() =>
                                                            w.task
                                                                ? applySubdomainRow(w.task, 'flashcard')
                                                                : applyDomainRow(w.domain, 'flashcard')
                                                        }
                                                        disabled={!w.domain && !w.task}
                                                    />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-[11px] text-amber-200/70 leading-snug line-clamp-2 break-words pl-1 border-l-2 border-amber-500/40 mb-0.5">
                                                            {w.domain}
                                                        </div>
                                                        {w.task != null && w.task !== '' ? (
                                                            <div className="leading-snug line-clamp-3 break-words pl-1 border-l-2 border-amber-500/50">
                                                                {w.task}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </div>
                                                <span className="shrink-0 font-mono text-amber-200 tabular-nums text-[11px]">
                                                    avg {w.avg_score}
                                                </span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {(
                            <>
                                <div className="rounded-lg border border-emerald-600/30 bg-emerald-950/20 p-4">
                                    <div className="flex items-center mb-3">
                                        <span className="text-lg font-semibold text-emerald-300">
                                            Evolution by 10 questions
                                        </span>
                                    </div>
                                    <div className="border-t border-emerald-600/30 mb-3" />
                                    <div className="text-xs text-gray-400 mb-2">
                                        Avg score % and validated QCM % with threshold guides.
                                    </div>
                                    {evolutionPoints.length === 0 && (
                                        <div className="text-xs text-gray-500 mb-2">
                                            No chunk data yet for this active session (first snapshot appears after 10 answers).
                                        </div>
                                    )}
                                    <div className="flex justify-center">
                                        <svg width={280} height={80} className="text-emerald-300/90">
                                            <line
                                                x1="6"
                                                x2="274"
                                                y1={(6 + (1 - qcmThresholdPct / 100) * 68).toFixed(1)}
                                                y2={(6 + (1 - qcmThresholdPct / 100) * 68).toFixed(1)}
                                                stroke="rgba(96,165,250,.95)"
                                                strokeDasharray="3 3"
                                            />
                                            <path d={evolutionAvgPath} fill="none" stroke="currentColor" strokeWidth="1.8" />
                                            <path
                                                d={evolutionValidatedPath}
                                                fill="none"
                                                stroke="rgba(96,165,250,.95)"
                                                strokeWidth="1.8"
                                            />
                                        </svg>
                                    </div>
                                    <div className="mt-2 flex items-center justify-between text-[11px] text-gray-300">
                                        <span>Validation threashold: {qcmThresholdPct.toFixed(0)}%</span>
                                        <span>Total questions number: {totalQuestionsNumber}</span>
                                    </div>
                                    <div className="mt-1 flex items-center gap-4 text-[11px] text-gray-300">
                                        <span className="inline-flex items-center gap-1">
                                            <span className="inline-block h-2 w-2 rounded-full bg-emerald-300" />
                                            Avg score %
                                        </span>
                                        <span className="inline-flex items-center gap-1">
                                            <span className="inline-block h-2 w-2 rounded-full bg-blue-400" />
                                            Validated QCM %
                                        </span>
                                    </div>
                                </div>

                                <div className="rounded-lg border border-blue-600/30 bg-blue-950/20 p-4">
                                    <div className="flex items-center mb-3">
                                        <span className="text-lg font-semibold text-blue-200/90">
                                            Forecast vs real
                                        </span>
                                    </div>
                                    <div className="border-t border-blue-600/30 mb-3" />
                                    <div className="text-xs text-gray-400 mb-2">
                                        By chunk of 10 questions, some metrics below.
                                    </div>
                                    <div className="flex justify-center">
                                        <svg width={sessionForecastChart.w} height={sessionForecastChart.h}>
                                            <line
                                                x1="16"
                                                x2="272"
                                                y1={sessionForecastChart.topBase}
                                                y2={sessionForecastChart.topBase}
                                                stroke="rgba(75,85,99,.9)"
                                            />
                                            <line
                                                x1="16"
                                                x2="272"
                                                y1={sessionForecastChart.thresholdY}
                                                y2={sessionForecastChart.thresholdY}
                                                stroke="rgba(251,191,36,.95)"
                                                strokeDasharray="3 3"
                                            />
                                            {sessionForecastChart.timeBars.map((b, i) => (
                                                <g key={`tf-${i}`}>
                                                    <rect x={b.x} y={b.yToReady} width={8} height={b.hToReady} rx="1.8" fill="rgba(147,197,253,.55)" />
                                                    <rect x={b.x} y={b.yInvested} width={8} height={b.hInvested} rx="1.8" fill="rgba(59,130,246,.92)" />
                                                    <text x={b.x + 4} y={sessionForecastChart.topBase + 10} fontSize="8" textAnchor="middle" fill="#cbd5e1">
                                                        {b.label}
                                                    </text>
                                                </g>
                                            ))}
                                            <path d={sessionForecastChart.validCurve} fill="none" stroke="rgba(251,191,36,.95)" strokeWidth="1.8" />

                                            <line x1="16" x2="272" y1={sessionForecastChart.botBase} y2={sessionForecastChart.botBase} stroke="rgba(75,85,99,.8)" />
                                            <path d={sessionForecastChart.errCurve} fill="none" stroke="rgba(217,70,239,.9)" strokeWidth="1.6" />
                                            <path d={sessionForecastChart.avgQTimeCurve} fill="none" stroke="rgba(52,211,153,.95)" strokeWidth="1.6" />
                                        </svg>
                                    </div>
                                    <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-gray-300">
                                        <div>Threshold: <span className="font-mono text-blue-200">{qcmThresholdPct.toFixed(0)}%</span></div>
                                    </div>
                                    <div className="mt-1 flex flex-wrap items-center gap-3 text-[10px] text-gray-400">
                                        <span><span className="inline-block h-2 w-2 rounded-full bg-blue-600 mr-1" />Time Invested</span>
                                        <span><span className="inline-block h-2 w-2 rounded-full bg-blue-300 mr-1" />Time to Ready</span>
                                        <span><span className="inline-block h-2 w-2 rounded-full bg-amber-300 mr-1" />validated QCM %</span>
                                        <span><span className="inline-block h-2 w-2 rounded-full bg-fuchsia-500 mr-1" />error sessions/chunk</span>
                                        <span><span className="inline-block h-2 w-2 rounded-full bg-emerald-400 mr-1" />avg time/question</span>
                                    </div>
                                </div>

                                <div className="rounded-lg border border-fuchsia-700/30 bg-fuchsia-950/20 p-4">
                                    <div className="flex items-center mb-3">
                                        <span className="text-lg font-semibold text-fuchsia-200/90">
                                            Weak answers
                                        </span>
                                    </div>
                                    <div className="border-t border-fuchsia-700/30 mb-3" />
                                    <div className="text-xs text-gray-400 mb-2">
                                        By chunk of 10 questions
                                    </div>
                                    <div className="text-[11px] text-gray-500 mb-2">
                                        Max weak answers per chunk: {weakLoadMax}
                                    </div>
                                    <div className="flex justify-center">
                                        <svg width={280} height={110}>
                                            <line
                                                x1="14"
                                                y1="90"
                                                x2="270"
                                                y2="90"
                                                stroke="rgba(75,85,99,.9)"
                                                strokeWidth="1"
                                            />
                                            {weakLoadBars.map((b, i) => (
                                                <g key={`weakbar-${i}`}>
                                                    <rect
                                                        x={b.x}
                                                        y={b.y}
                                                        width={b.w}
                                                        height={b.h}
                                                        rx="2"
                                                        fill="rgba(217,70,239,.82)"
                                                    />
                                                    <text
                                                        x={b.x + b.w / 2}
                                                        y={100}
                                                        fill="#d1d5db"
                                                        fontSize="9"
                                                        textAnchor="middle"
                                                    >
                                                        {i + 1}
                                                    </text>
                                                </g>
                                            ))}
                                        </svg>
                                    </div>
                                    <div className="text-[11px] text-gray-500 mt-1">
                                        X: chunk index · Y: weak answers count
                                    </div>
                                </div>

                                <div className="rounded-lg border border-violet-700/30 bg-violet-950/20 p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-lg font-semibold text-violet-200/90">
                                            Sub-domain score distribution
                                        </span>
                                        <span className="text-xs text-violet-100/70">
                                            threshold {validationThreshold}/10
                                        </span>
                                    </div>
                                    <div className="border-t border-violet-700/30 mb-3" />
                                    <div className="h-3 rounded bg-gray-800 overflow-hidden flex">
                                        <div className="bg-emerald-500/80" style={{ width: `${scoreBands.highPct}%` }} />
                                        <div className="bg-yellow-500/80" style={{ width: `${scoreBands.midPct}%` }} />
                                        <div className="bg-rose-500/80" style={{ width: `${scoreBands.lowPct}%` }} />
                                    </div>
                                    <div className="mt-2 grid grid-cols-3 gap-2 text-[11px] text-gray-300">
                                        <div>High: {scoreBands.high}</div>
                                        <div>Medium: {scoreBands.mid}</div>
                                        <div>Low: {scoreBands.low}</div>
                                    </div>
                                </div>

                                <div className="rounded-lg border border-lime-700/30 bg-lime-950/15 p-4">
                                    <div className="flex items-center mb-3">
                                        <span className="text-lg font-semibold text-lime-200/90">
                                            Domain heat ranking
                                        </span>
                                    </div>
                                    <div className="border-t border-lime-700/30 mb-3" />
                                    <div className="space-y-2">
                                        {domainHeatRows.map((r) => (
                                            <div key={`heat-${r.domain}`} className="text-xs">
                                                <div className="flex justify-between mb-1 text-gray-300">
                                                    <span className="truncate pr-2">{r.domain}</span>
                                                    <span className="font-mono">{r.avg_score}</span>
                                                </div>
                                                <div className="h-2 rounded bg-gray-800 overflow-hidden">
                                                    <div className="h-full bg-lime-500/75" style={{ width: `${r.heatPct}%` }} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="rounded-lg border border-sky-700/30 bg-sky-950/20 p-4">
                                    <div className="flex items-center mb-3">
                                        <span className="text-lg font-semibold text-sky-200/90">
                                            Latest chunk weak rate
                                        </span>
                                    </div>
                                    <div className="border-t border-sky-700/30 mb-3" />
                                    <div className="flex items-center gap-4">
                                        <svg width={74} height={74} viewBox="0 0 74 74">
                                            <circle cx="37" cy="37" r="30" stroke="rgba(31,41,55,.8)" strokeWidth="8" fill="none" />
                                            <circle
                                                cx="37"
                                                cy="37"
                                                r="30"
                                                stroke="rgba(56,189,248,.9)"
                                                strokeWidth="8"
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeDasharray={`${(weakRatePct / 100) * 188.4} 188.4`}
                                                transform="rotate(-90 37 37)"
                                            />
                                            <text x="37" y="41" textAnchor="middle" fill="#bae6fd" fontSize="12" fontWeight="600">
                                                {weakRatePct}%
                                            </text>
                                        </svg>
                                        <div className="text-xs text-gray-300">
                                            Weak answers in latest 10-question block.
                                            <div className="text-gray-500 mt-1">
                                                Target: keep this rate trending down.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {makeupPoints.length > 0 && (
                            <>
                                <div className="rounded-lg border border-rose-700/30 bg-rose-950/20 p-4">
                                    <div className="flex items-center mb-3">
                                        <span className="text-lg font-semibold text-rose-200/90">
                                            Make-up load evolution
                                        </span>
                                    </div>
                                    <div className="border-t border-rose-700/30 mb-3" />
                                    <div className="text-xs text-gray-400 mb-2">
                                        Two curves: initial make-up volume vs remaining make-up volume.
                                    </div>
                                    <div className="flex justify-center">
                                        <svg width={280} height={80}>
                                            <path d={makeupCurvePaths.initial} fill="none" stroke="rgba(251,113,133,.9)" strokeWidth="1.7" />
                                            <path d={makeupCurvePaths.remaining} fill="none" stroke="rgba(45,212,191,.95)" strokeWidth="1.7" />
                                        </svg>
                                    </div>
                                    <div className="mt-2 flex items-center gap-4 text-[11px] text-gray-300">
                                        <span className="inline-flex items-center gap-1">
                                            <span className="inline-block h-2 w-2 rounded-full bg-rose-400" />
                                            Initial
                                        </span>
                                        <span className="inline-flex items-center gap-1">
                                            <span className="inline-block h-2 w-2 rounded-full bg-teal-400" />
                                            Remaining
                                        </span>
                                    </div>
                                </div>

                                <div className="rounded-lg border border-amber-700/30 bg-amber-950/20 p-4">
                                    <div className="flex items-center mb-3">
                                        <span className="text-lg font-semibold text-amber-200/90">
                                            Make-up by 10-question blocks
                                        </span>
                                    </div>
                                    <div className="border-t border-amber-700/30 mb-3" />
                                    <div className="space-y-2 max-h-56 overflow-y-auto">
                                        {makeupBuckets.map((b) => {
                                            const maxVal = Math.max(
                                                1,
                                                ...makeupBuckets.map((x) =>
                                                    Math.max(Number(x.initial_total || 0), Number(x.remaining_total || 0))
                                                )
                                            );
                                            const initW = Math.max(2, Math.round((Number(b.initial_total || 0) / maxVal) * 100));
                                            const remW = Math.max(2, Math.round((Number(b.remaining_total || 0) / maxVal) * 100));
                                            return (
                                                <div key={`mkb-${b.bucket_index}`} className="text-xs">
                                                    <div className="flex justify-between mb-1 text-gray-300">
                                                        <span>{b.label}</span>
                                                        <span className="font-mono">
                                                            I:{b.initial_total} · R:{b.remaining_total}
                                                        </span>
                                                    </div>
                                                    <div className="h-2 rounded bg-gray-800 overflow-hidden mb-1">
                                                        <div className="h-full bg-rose-500/75" style={{ width: `${initW}%` }} />
                                                    </div>
                                                    <div className="h-2 rounded bg-gray-800 overflow-hidden">
                                                        <div className="h-full bg-teal-500/75" style={{ width: `${remW}%` }} />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                )}

                {!loading && timeline && (
                    <div className="rounded-lg border border-gray-700/80 bg-gray-900/40 p-4">
                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            Make-up sessions (cumulative)
                        </div>
                        <div className="mt-1 text-[11px] text-gray-500">
                            X = days since first session · Y = cumulative count
                            {timeline.t0 && (
                                <span className="ml-1 text-gray-600">· t₀ {new Date(timeline.t0).toLocaleDateString()}</span>
                            )}
                        </div>
                        <div className="mt-3 flex justify-center">
                            <Sparkline points={timeline.points || []} />
                        </div>
                        {timeline.inferred_without_timestamps && (
                            <div className="mt-2 text-[11px] text-amber-200/80">
                                Older sessions without timestamps — day index approximated by order.
                            </div>
                        )}
                    </div>
                )}

                {!loading && ongoing && (
                    <>
                        <div className="rounded-lg border border-orange-600/35 bg-orange-950/20 p-4">
                            <div className="flex items-center mb-3">
                                <svg
                                    className="w-5 h-5 text-orange-400 mr-2 shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                                <span className="text-lg font-semibold text-orange-200/90">Ongoing make-up (by domain)</span>
                            </div>
                            <div className="border-t border-orange-600/30 mb-3" />
                            <div className="text-xs text-gray-400 mb-2">
                                Sorted by remaining questions (desc). Remaining uses{' '}
                                <code className="text-orange-200/80">make-up_questions_nb</code>
                            </div>
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                                {ongoing.by_domain?.length ? (
                                    ongoing.by_domain.map((row) => (
                                        <div
                                            key={row.domain}
                                            className="flex items-start justify-between gap-2 border-b border-orange-600/20 pb-2 last:border-0"
                                        >
                                            <div className="flex min-w-0 flex-1 items-start gap-1">
                                                <DashRowActions
                                                    onQcm={() => applyDomainRow(row.domain, 'qcm')}
                                                    onFlash={() => applyDomainRow(row.domain, 'flashcard')}
                                                    disabled={!row.domain}
                                                />
                                                <span className="min-w-0 flex-1 text-xs text-orange-100/90 leading-snug line-clamp-2 break-words pl-1 border-l-2 border-orange-500/45">
                                                    {row.domain}
                                                </span>
                                            </div>
                                            <div className="flex shrink-0 items-baseline gap-2 text-xs tabular-nums">
                                                <span className="text-gray-500">{row.sessions} sess.</span>
                                                <span className="text-orange-200 font-mono font-semibold w-10 text-right">
                                                    {row.remaining_questions} Q
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-xs text-gray-500">No active make-up sessions.</div>
                                )}
                            </div>
                        </div>

                        <div className="rounded-lg border border-orange-600/35 bg-orange-950/20 p-4">
                            <div className="flex items-center mb-3">
                                <svg
                                    className="w-5 h-5 text-orange-400 mr-2 shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 6h16M4 10h16M4 14h10M4 18h10"
                                    />
                                </svg>
                                <span className="text-lg font-semibold text-orange-200/90">
                                    Ongoing make-up (domain + sub-domain)
                                </span>
                            </div>
                            <div className="border-t border-orange-600/30 mb-3" />
                            <div className="text-xs text-gray-400 mb-2">Sorted by remaining questions (desc).</div>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {ongoing.by_domain_task?.length ? (
                                    ongoing.by_domain_task.map((row, idx) => (
                                        <div
                                            key={`${row.domain}|${row.task}|${idx}`}
                                            className="border-b border-orange-600/20 pb-2 last:border-0"
                                        >
                                            <div className="text-[11px] text-orange-200/70 leading-snug line-clamp-2 break-words pl-1 border-l-2 border-orange-500/40 mb-1">
                                                {row.domain}
                                            </div>
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex min-w-0 flex-1 items-start gap-1">
                                                    <DashRowActions
                                                        onQcm={() => applySubdomainRow(row.task, 'qcm')}
                                                        onFlash={() => applySubdomainRow(row.task, 'flashcard')}
                                                        disabled={!row.task}
                                                    />
                                                    <span className="min-w-0 flex-1 text-xs text-orange-100/90 leading-snug line-clamp-2 break-words pl-1 border-l-2 border-orange-500/30">
                                                        {row.task}
                                                    </span>
                                                </div>
                                                <span className="shrink-0 text-orange-200 font-mono font-semibold text-xs tabular-nums w-10 text-right">
                                                    {row.remaining_questions} Q
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-xs text-gray-500">None.</div>
                                )}
                            </div>
                        </div>

                        <div className="rounded-lg border border-orange-600/35 bg-orange-950/20 p-4">
                            <div className="flex items-center mb-3">
                                <svg
                                    className="w-5 h-5 text-orange-400 mr-2 shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                                    />
                                </svg>
                                <span className="text-lg font-semibold text-orange-200/90">Ongoing by keyword groups</span>
                            </div>
                            <div className="border-t border-orange-600/30 mb-3" />
                            <div className="text-xs text-gray-400 mb-2">Sorted by remaining questions (desc).</div>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {ongoing.by_keywords?.length ? (
                                    ongoing.by_keywords.map((row) => {
                                        const canKeyword = granularKeysForKeywordRow(uid, row.keywords_key).length > 0;
                                        return (
                                            <div
                                                key={row.keywords_key}
                                                className="flex justify-between gap-2 border-b border-orange-600/20 pb-2 last:border-0"
                                            >
                                                <div className="flex min-w-0 flex-1 items-start gap-1">
                                                    <DashRowActions
                                                        onQcm={() => applyKeywordRow(row.keywords_key, 'qcm')}
                                                        onFlash={() => applyKeywordRow(row.keywords_key, 'flashcard')}
                                                        disabled={!canKeyword}
                                                    />
                                                    <span
                                                        className="min-w-0 text-xs text-orange-100/85 leading-snug line-clamp-2 break-words pl-1 border-l-2 border-orange-500/35"
                                                        title={row.keywords_key}
                                                    >
                                                        {row.keywords_key.replace(/\|/g, ' · ')}
                                                    </span>
                                                </div>
                                                <span className="text-orange-200 font-mono shrink-0 text-xs font-semibold tabular-nums w-10 text-right">
                                                    {row.remaining_questions} Q
                                                </span>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="text-xs text-gray-500">None.</div>
                                )}
                            </div>
                        </div>

                        <div className="rounded-lg border border-indigo-900/40 bg-indigo-950/20 p-4">
                            <div className="text-xs font-medium uppercase tracking-wide text-indigo-200/80">
                                Extra
                            </div>
                            <ul className="mt-2 list-disc pl-4 text-sm text-gray-400 space-y-1">
                                <li>Active make-up sessions: {ongoing.active_sessions_count}</li>
                                <li>
                                    Initial question counts are stored as{' '}
                                    <code className="text-gray-500">initial_makeup_questions_nb</code> (unchanged after creation).
                                </li>
                            </ul>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default MobileDashboard;
